/**
 * ReworkModal - Report rework occurrences
 *
 * Unified rework tracking that collects:
 * - Quote, Order, Job/SKU
 * - WHAT needs to be redone (reprint, opti, assembly, etc.)
 * - CAUSE (root cause)
 * - Estimated TIME
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { REWORK_TYPES, REWORK_CAUSES, TIME_ESTIMATES, getCausesByCategory } from '../config/reworkConstants';

const API_BASE = import.meta.env.VITE_API_URL || '';

// Rework vs Revision classification (for billing purposes)
const REPORT_TYPES = [
  { id: 'rework', label: 'Rework (Internal Fix)', description: 'Error caught internally - needs correction' },
  { id: 'revision', label: 'Revision (Client Request)', description: 'Client requested changes after approval' },
];

export default function ReworkModal({ isOpen, onClose, employeeId, displayName, token }) {
  // Report type (rework vs revision)
  const [reportType, setReportType] = useState('');

  // WHAT - type of rework needed
  const [reworkWhat, setReworkWhat] = useState('');

  // CAUSE - root cause
  const [cause, setCause] = useState('');

  // Description and notes
  const [description, setDescription] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  // TIME estimate
  const [estimatedMinutes, setEstimatedMinutes] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // Project/Quote selector state
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const projectInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Order selector state
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Job selector state
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(false);

  const causesByCategory = getCausesByCategory();

  // Fetch active projects on open
  useEffect(() => {
    if (isOpen && projects.length === 0) {
      fetchProjects();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          projectInputRef.current && !projectInputRef.current.contains(e.target)) {
        setShowProjectDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch orders when project is selected
  useEffect(() => {
    if (selectedProject?.quoteNumber) {
      fetchOrders(selectedProject.quoteNumber);
    } else {
      setOrders([]);
      setSelectedOrder(null);
      setJobs([]);
      setSelectedJobs({});
    }
  }, [selectedProject?.quoteNumber]);

  // Fetch jobs when order is selected
  useEffect(() => {
    if (selectedOrder?.orderNumber) {
      fetchJobs(selectedOrder.orderNumber);
    } else {
      setJobs([]);
      setSelectedJobs({});
    }
  }, [selectedOrder?.orderNumber]);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await fetch(`${API_BASE}/api/quotes?activeOnly=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        const quotes = data.quotes || data || [];
        const projectList = quotes.map(q => ({
          id: q.quoteNumber || q.Quote,
          quoteNumber: q.quoteNumber || q.Quote,
          projectName: q.projectName || q.user_Text4 || q.Part_Number || q.Description || 'Unnamed Project',
          customer: q.customer || q.Customer,
        }));
        setProjects(projectList);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchOrders = async (quoteNumber) => {
    setLoadingOrders(true);
    try {
      const response = await fetch(`${API_BASE}/api/orders?quoteNumber=${encodeURIComponent(quoteNumber)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        const orderList = data.orders || [];
        setOrders(orderList);
        // Auto-select if only one order
        if (orderList.length === 1) {
          setSelectedOrder(orderList[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchJobs = async (orderNumber) => {
    setLoadingJobs(true);
    try {
      const response = await fetch(`${API_BASE}/api/orders/${orderNumber}/jobs`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  // Filter projects based on search
  const filteredProjects = useCallback(() => {
    if (!projectSearch.trim()) return projects.slice(0, 20);

    const search = projectSearch.toLowerCase();
    return projects
      .filter(p =>
        p.quoteNumber?.toLowerCase().includes(search) ||
        p.projectName?.toLowerCase().includes(search) ||
        p.customer?.toLowerCase().includes(search)
      )
      .slice(0, 20);
  }, [projectSearch, projects]);

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setProjectSearch(project.projectName || project.quoteNumber);
    setShowProjectDropdown(false);
  };

  const handleClearProject = () => {
    setSelectedProject(null);
    setProjectSearch('');
    setSelectedOrder(null);
    setOrders([]);
    setJobs([]);
    setSelectedJobs({});
  };

  const handleToggleJob = (job) => {
    setSelectedJobs(prev => {
      if (prev[job.id]) {
        const { [job.id]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [job.id]: job };
    });
  };

  const handleSelectAllJobs = () => {
    const all = {};
    jobs.forEach(j => { all[j.id] = j; });
    setSelectedJobs(all);
  };

  const handleClearJobSelection = () => {
    setSelectedJobs({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reportType || !reworkWhat || !cause || !description || !selectedProject) return;

    setSubmitting(true);
    try {
      const selectedJobList = Object.values(selectedJobs);

      const response = await fetch(`${API_BASE}/api/rework`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          reportType,
          reworkWhat,
          cause,
          quoteNumber: selectedProject?.quoteNumber,
          orderNumber: selectedOrder?.orderNumber || null,
          projectName: selectedProject?.projectName,
          jobs: selectedJobList.length > 0 ? selectedJobList.map(j => ({
            jobId: j.id,
            jobNumber: j.jobNumber,
            partNumber: j.partNumber,
            description: j.description || j.name,
            qty: j.qty,
          })) : null,
          description,
          additionalNotes: additionalNotes || null,
          estimatedMinutes: estimatedMinutes ? parseInt(estimatedMinutes) : null,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit rework report');
      }

      console.log('Rework reported successfully');
      handleClose();
    } catch (err) {
      console.error('Error submitting rework:', err);
      alert('Failed to submit rework report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReportType('');
    setReworkWhat('');
    setCause('');
    setSelectedProject(null);
    setProjectSearch('');
    setSelectedOrder(null);
    setOrders([]);
    setJobs([]);
    setSelectedJobs({});
    setDescription('');
    setAdditionalNotes('');
    setEstimatedMinutes('');
    onClose();
  };

  const filtered = filteredProjects();
  const selectedJobCount = Object.keys(selectedJobs).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Track Rework"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
        {/* Report Type Selection (Rework vs Revision) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Type <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {REPORT_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setReportType(type.id)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  reportType === type.id
                    ? type.id === 'rework'
                      ? 'border-red-500 bg-red-50 text-red-900'
                      : 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-slate-200'
                }`}
              >
                <div className="font-medium text-sm">{type.label}</div>
                <div className="text-xs text-gray-500 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Project/Quote Selector */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quote/Project <span className="text-red-500">*</span>
          </label>

          {selectedProject ? (
            <div className="flex items-center justify-between px-4 py-2.5 bg-slate-200 border border-gray-200 rounded-lg">
              <div>
                <div className="text-sm text-gray-900">{selectedProject.projectName}</div>
                <div className="text-xs text-gray-500">
                  Quote: {selectedProject.quoteNumber}
                  {selectedProject.customer && ` - ${selectedProject.customer}`}
                </div>
              </div>
              <button type="button" onClick={handleClearProject} className="p-1 text-gray-500 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <>
              <input
                ref={projectInputRef}
                type="text"
                value={projectSearch}
                onChange={(e) => {
                  setProjectSearch(e.target.value);
                  setShowProjectDropdown(true);
                }}
                onFocus={() => setShowProjectDropdown(true)}
                placeholder={loadingProjects ? "Loading..." : "Search by quote #, project name..."}
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              />
              {showProjectDropdown && (
                <div ref={dropdownRef} className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {loadingProjects ? (
                    <div className="px-4 py-3 text-sm text-gray-500">Loading...</div>
                  ) : filtered.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-gray-500">No matching projects</div>
                  ) : (
                    filtered.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => handleSelectProject(project)}
                        className="w-full text-left px-4 py-2.5 hover:bg-slate-200 transition-colors border-b border-gray-100 last:border-0"
                      >
                        <div className="text-sm text-gray-900 truncate">{project.projectName}</div>
                        <div className="text-xs text-gray-500">Quote: {project.quoteNumber}</div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Order Selector */}
        {selectedProject && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Order <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            {loadingOrders ? (
              <div className="text-gray-500 text-sm">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="text-gray-500 text-sm">No orders for this quote</div>
            ) : (
              <select
                value={selectedOrder?.orderNumber || ''}
                onChange={(e) => {
                  const order = orders.find(o => o.orderNumber === e.target.value);
                  setSelectedOrder(order || null);
                }}
                className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
              >
                <option value="">Select an order...</option>
                {orders.map((o) => (
                  <option key={o.orderNumber} value={o.orderNumber}>
                    {o.orderNumber} - {o.projectName || 'No name'}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Job/SKU Selector */}
        {selectedOrder && jobs.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Jobs/SKUs <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <div className="flex gap-2 text-xs">
                <button type="button" onClick={handleSelectAllJobs} className="text-purple-600 hover:text-purple-800">Select All</button>
                <span className="text-gray-300">|</span>
                <button type="button" onClick={handleClearJobSelection} className="text-gray-600 hover:text-gray-800">Clear</button>
              </div>
            </div>
            {loadingJobs ? (
              <div className="text-gray-500 text-sm">Loading jobs...</div>
            ) : (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-40 overflow-y-auto">
                {jobs.map((job) => {
                  const isSelected = !!selectedJobs[job.id];
                  return (
                    <div
                      key={job.id}
                      className={`p-2.5 flex items-center gap-3 cursor-pointer ${isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                      onClick={() => handleToggleJob(job)}
                    >
                      <input type="checkbox" checked={isSelected} readOnly className="h-4 w-4 text-purple-600 rounded border-gray-300" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{job.jobNumber}</div>
                        <div className="text-xs text-gray-500 truncate">{job.partNumber} - {job.description || job.name}</div>
                      </div>
                      <div className="text-xs text-gray-400">Qty: {job.qty}</div>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedJobCount > 0 && (
              <div className="mt-1 text-xs text-purple-600">{selectedJobCount} job(s) selected</div>
            )}
          </div>
        )}

        {/* WHAT - What needs to be redone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What needs to be redone? <span className="text-red-500">*</span>
          </label>
          <select
            value={reworkWhat}
            onChange={(e) => setReworkWhat(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
          >
            <option value="">Select type of rework...</option>
            {REWORK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          {reworkWhat && (
            <p className="text-xs text-gray-500 mt-1">
              {REWORK_TYPES.find(t => t.value === reworkWhat)?.description}
            </p>
          )}
        </div>

        {/* CAUSE - Root cause */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            What caused this? <span className="text-red-500">*</span>
          </label>
          <select
            value={cause}
            onChange={(e) => setCause(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
          >
            <option value="">Select a cause...</option>
            {Object.entries(causesByCategory).map(([categoryKey, category]) => (
              <optgroup key={categoryKey} label={category.label}>
                {category.causes.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Describe the issue <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What went wrong and what needs to be fixed..."
            rows={3}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
          />
        </div>

        {/* TIME Estimate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Estimated time to fix <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <select
            value={estimatedMinutes}
            onChange={(e) => setEstimatedMinutes(e.target.value)}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
          >
            <option value="">Select estimate...</option>
            {TIME_ESTIMATES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Additional Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Additional notes <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            placeholder="Any additional context..."
            rows={2}
            className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
          />
        </div>

        {/* Actions */}
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!reportType || !reworkWhat || !cause || !description || !selectedProject || submitting}
            loading={submitting}
          >
            Submit Report
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
